import { createCircle, darken } from './svgPrims';
import type { NpcProp } from './HumanoidTypes';
import type { PropContext, PropLayers } from './NpcProps';

/**
 * Plan 12 role vocabulary props — what a job looks like at a glance: an
 * elder's candle, a naturalist's jar, a banker's ledger and keys, a captain's
 * shield, a rogue's daggers, a shaman's totem. Canonical 140×120 space, the
 * figure faces right (front hand ≈ x 104–116, y 70). Held props draw behind
 * the arm so the hand visibly grips them.
 */
export type RoleProp = Extract<NpcProp, { kind: 'candle' | 'jar' | 'book' | 'key_ring' | 'hammer' | 'shield' | 'daggers' | 'totem' }>;

const ROLE_PROP_KINDS = new Set(['candle', 'jar', 'book', 'key_ring', 'hammer', 'shield', 'daggers', 'totem']);

export function isRoleProp(prop: NpcProp): prop is RoleProp {
  return ROLE_PROP_KINDS.has(prop.kind);
}

export function renderRoleProp(prop: RoleProp, ctx: PropContext, out: PropLayers): void {
  const { m } = ctx;
  const hx = ctx.frontHand.x;
  const hy = ctx.frontHand.y;
  switch (prop.kind) {
  case 'hammer': {
    // Short haft behind the gripping hand; the broad head stays beside the face.
    out[ctx.fit ? 'back' : 'front'] += `<g data-npc-prop="hammer">
      <path d="M${hx + 1},${hy + 17} L${hx + 1},${hy - 14}" stroke="${darken(prop.color, 0.65)}" stroke-width="5" stroke-linecap="round" />
      <path d="M${hx},${hy + 15} L${hx},${hy - 12}" stroke="${prop.color}" stroke-width="2.4" stroke-linecap="round" />
      <rect x="${hx - 11}" y="${hy - 22}" width="26" height="11" rx="2" fill="${ctx.paint.fill(prop.headColor)}" stroke="${darken(prop.headColor, 0.6)}" stroke-width="1.5" />
      <path d="M${hx - 8},${hy - 19} H${hx + 11}" stroke="#e1e4dc" stroke-width="1.4" opacity="0.75" />
    </g>`;
    break;
  }
  case 'candle': {
    const flame = prop.flameColor ?? '#ffcf6a';
    out[ctx.fit ? 'back' : 'front'] += `<g data-npc-prop="candle">
      ${createCircle(hx + 1, hy - 20, 11, flame, 'none', 0, 0.2)}
      <ellipse cx="${hx + 1}" cy="${hy + 3}" rx="7" ry="2.4" fill="${darken(prop.color, 0.7)}" />
      <rect x="${hx - 2}" y="${hy - 14}" width="6" height="16" rx="1.5" fill="#efe6cf" />
      <path d="M${hx + 1},${hy - 26} Q${hx + 5},${hy - 19} ${hx + 1},${hy - 15} Q${hx - 3},${hy - 19} ${hx + 1},${hy - 26} Z" fill="${flame}" />
      <path d="M${hx + 1},${hy - 21} Q${hx + 2.5},${hy - 18} ${hx + 1},${hy - 16} Q${hx - 0.5},${hy - 18} ${hx + 1},${hy - 21} Z" fill="#fff6d8" />
    </g>`;
    break;
  }
  case 'jar':
    out[ctx.fit ? 'back' : 'front'] += `<g data-npc-prop="jar">
      <rect x="${hx - 6}" y="${hy - 16}" width="14" height="18" rx="4" fill="#cfe8ec" opacity="0.55" />
      <rect x="${hx - 5}" y="${hy - 9}" width="12" height="10" rx="3" fill="${prop.liquidColor}" opacity="0.75" />
      <path d="M${hx - 1},${hy - 4} q2,-3 4,0 q-2,2 -4,0 Z M${hx + 3},${hy - 6} l2,-2" fill="#e86a3a" stroke="#e86a3a" stroke-width="0.8" />
      <rect x="${hx - 7}" y="${hy - 19}" width="16" height="4" rx="1.5" fill="#8a6a44" />
      <path d="M${hx - 3},${hy - 13} L${hx - 3},${hy - 3}" stroke="#ffffff" stroke-width="1.2" opacity="0.7" />
    </g>`;
    break;
  case 'book': {
    const glow = prop.glowColor;
    out[ctx.fit ? 'back' : 'front'] += `<g data-npc-prop="${glow ? 'spellbook' : 'ledger'}" transform="rotate(-12 ${hx} ${hy})">
      ${glow ? createCircle(hx, hy - 6, 13, glow, 'none', 0, 0.22) : ''}
      <path d="M${hx - 12},${hy - 4} L${hx},${hy - 1} L${hx + 12},${hy - 4} L${hx + 12},${hy + 8} L${hx},${hy + 11} L${hx - 12},${hy + 8} Z" fill="${prop.color}" />
      <path d="M${hx - 10},${hy - 5} L${hx},${hy - 2.5} L${hx + 10},${hy - 5} L${hx + 10},${hy + 6} L${hx},${hy + 8.5} L${hx - 10},${hy + 6} Z" fill="#f2ead2" />
      <path d="M${hx - 8},${hy} L${hx - 2},${hy + 1.5} M${hx - 8},${hy + 3} L${hx - 2},${hy + 4.5} M${hx + 2},${hy + 1.5} L${hx + 8},${hy} M${hx + 2},${hy + 4.5} L${hx + 8},${hy + 3}" stroke="${glow ?? '#8a7a5a'}" stroke-width="1.1" />
    </g>`;
    break;
  }
  case 'key_ring': {
    const x = m.left + 26;
    out.front += `<g data-npc-prop="key_ring">
      <circle cx="${x}" cy="88" r="5" fill="none" stroke="${prop.color}" stroke-width="2" />
      <path d="M${x - 3},92 l-3,9 l3,0 M${x + 1},93 l1,10 l3,-1 M${x + 4},91 l5,8 l2,-2" stroke="${prop.color}" stroke-width="2" fill="none" stroke-linecap="round" />
    </g>`;
    break;
  }
  case 'shield': {
    // Carried on the back arm but drawn over the body edge so wide bodies don't hide it.
    // Fitted: strapped to the back arm at the back hand; otherwise over the body edge.
    const x = ctx.fit && ctx.backHand ? ctx.backHand.x - 2 : m.left + 4;
    const cy = ctx.fit && ctx.backHand ? ctx.backHand.y - 2 : 73;
    out.front += `<g data-npc-prop="shield">
      <ellipse cx="${x}" cy="${cy + 1}" rx="15" ry="18" fill="${darken(prop.color, 0.75)}" />
      <ellipse cx="${x}" cy="${cy}" rx="13" ry="16" fill="${ctx.paint.fill(prop.color)}" />
      <ellipse cx="${x}" cy="${cy}" rx="13" ry="16" fill="none" stroke="${prop.accentColor}" stroke-width="2.4" />
      <path d="M${x},${cy - 14} L${x},${cy + 14} M${x - 11},${cy} L${x + 11},${cy}" stroke="${prop.accentColor}" stroke-width="1.6" opacity="0.7" />
      ${createCircle(x, cy, 3.6, prop.accentColor)}
    </g>`;
    break;
  }
  case 'daggers': {
    // A pair worn low at the hip, hilts up, below the belt line.
    const x = m.right - 14;
    out.front += `<g data-npc-prop="daggers">
      <path d="M${x - 3},86 L${x - 7},104 M${x + 3},86 L${x + 5},104" stroke="${prop.color}" stroke-width="2.6" stroke-linecap="round" />
      <path d="M${x - 6},86 L${x},86 M${x},86 L${x + 6},86" stroke="#5a3a20" stroke-width="3.2" stroke-linecap="round" />
      <path d="M${x - 2},81 L${x - 3},86 M${x + 2},81 L${x + 3},86" stroke="#5a3a20" stroke-width="2.6" stroke-linecap="round" />
    </g>`;
    break;
  }
  case 'totem': {
    const x = hx + 3;
    out.back += `<g data-npc-prop="totem">
      <path d="M${x},114 L${x},20" stroke="${darken(prop.color, 0.7)}" stroke-width="4.2" stroke-linecap="round" />
      <rect x="${x - 7}" y="6" width="14" height="16" rx="3" fill="${prop.color}" />
      <path d="M${x - 4},11 h3 M${x + 1},11 h3 M${x - 3},17 q3,2 6,0" stroke="${darken(prop.color, 0.5)}" stroke-width="1.4" fill="none" />
      <path d="M${x - 7},12 Q${x - 16},14 ${x - 18},24 Q${x - 11},20 ${x - 7},16 Z M${x + 7},12 Q${x + 16},14 ${x + 18},24 Q${x + 11},20 ${x + 7},16 Z" fill="${prop.featherColor}" />
    </g>`;
    out.front += `<path data-npc-prop="totem-glint" d="M${x + 3},-2 L${x - 1},5 L${x + 2},5 L${x - 2},12" fill="none" stroke="${prop.glintColor}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />`;
    break;
  }
  }
}
